import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { ChevronLeft, ChevronRight, X, Check, ArrowLeft } from "lucide-react";
import { LANGS, RTL_LANGS, UI, CH_UI, chaptersForRole } from "@/lib/onboarding";

const DONE_KEY = "slackr_onb_done";

export default function OnboardingSlideshow({ isAdmin, onClose }) {
  const [lang, setLang] = useState(() => localStorage.getItem("slackr_lang") || "nl");
  const chapters = useMemo(() => chaptersForRole(isAdmin), [isAdmin]);
  const [view, setView] = useState("menu"); // "menu" | "reading"
  const [chIdx, setChIdx] = useState(0);
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DONE_KEY) || "[]")); } catch { return new Set(); }
  });

  const t = UI[lang];
  const c = CH_UI[lang];
  const rtl = RTL_LANGS.includes(lang);
  const pickLang = (l) => { setLang(l); localStorage.setItem("slackr_lang", l); };
  const allDone = chapters.every((ch) => done.has(ch.id));

  const markDone = (id) => {
    const next = new Set(done); next.add(id); setDone(next);
    localStorage.setItem(DONE_KEY, JSON.stringify([...next]));
    return next;
  };

  const openChapter = (i) => { setChIdx(i); setIdx(0); setView("reading"); };

  const Back = rtl ? ChevronRight : ChevronLeft;
  const Fwd = rtl ? ChevronLeft : ChevronRight;

  // ---------- MENU ----------
  if (view === "menu") {
    return (
      <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-overlay">
        <div dir={rtl ? "rtl" : "ltr"} className="bg-brand-cream w-full max-w-md rounded-2xl brutal border-2 border-brand-ink overflow-hidden flex flex-col max-h-[92vh]">
          <div className="p-5 bg-brand-violet text-white relative">
            <button data-testid="onboarding-skip" onClick={() => onClose(allDone)}
              className="absolute top-3 end-3 bg-white/25 hover:bg-white/40 rounded-full p-1.5"><X className="w-4 h-4" strokeWidth={2.5} /></button>
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="onboarding-langs">
              {LANGS.map((l) => (
                <button key={l.code} data-testid={`onboarding-lang-${l.code}`} onClick={() => pickLang(l.code)}
                  className={`rounded-full px-2.5 py-1 text-xs font-bold brutal-sm ${lang === l.code ? "bg-white text-brand-ink" : "bg-white/25"}`}>{l.flag} {l.label}</button>
              ))}
            </div>
            <p className="text-xs font-bold uppercase tracking-widest opacity-80">{allDone ? c.allDoneTitle : (isAdmin ? t.adminTitle : t.userTitle)}</p>
            <h2 className="font-head font-black text-2xl leading-tight" data-testid="onboarding-menu-title">{c.menuTitle}</h2>
            <p className="text-sm font-semibold opacity-90 mt-1">{c.menuSub}</p>
          </div>

          <div className="p-4 overflow-y-auto flex-1 space-y-2.5" data-testid="onboarding-chapters">
            {chapters.map((ch, i) => {
              const Icon = Icons[ch.icon] || Icons.Sparkles;
              const isDone = done.has(ch.id);
              return (
                <button key={ch.id} data-testid={`onboarding-chapter-${ch.id}`} onClick={() => openChapter(i)}
                  className="w-full brutal-sm brutal-press rounded-2xl bg-white p-3 flex items-center gap-3 text-start">
                  <span className={`w-11 h-11 rounded-xl ${ch.color} flex items-center justify-center shrink-0`}><Icon className="w-6 h-6" strokeWidth={2.5} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="font-head font-black block leading-tight">{i + 1}. {ch.title[lang]}</span>
                    <span className="text-xs text-brand-ink/60 block truncate">{ch.sub[lang]}</span>
                  </span>
                  {isDone
                    ? <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-brand-ink bg-brand-green rounded-full px-2 py-1"><Check className="w-3.5 h-3.5" strokeWidth={3} />{c.completed}</span>
                    : <span className="shrink-0 text-xs font-bold text-brand-ink/40">{ch.slideList.length} <Fwd className="w-4 h-4 inline" strokeWidth={2.5} /></span>}
                </button>
              );
            })}
          </div>

          <div className="p-4 border-t-2 border-brand-ink/10 bg-white flex items-center justify-between gap-2">
            <button data-testid="onboarding-later" onClick={() => onClose(allDone)}
              className="rounded-xl px-4 py-2.5 font-head font-bold text-brand-ink/60">{c.later}</button>
            {allDone && (
              <button data-testid="onboarding-done" onClick={() => onClose(true)}
                className="brutal brutal-press rounded-xl px-5 py-2.5 bg-brand-green font-head font-bold">{t.done}</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- READING ----------
  const ch = chapters[chIdx];
  const slides = ch.slideList;
  const slide = slides[idx];
  const Icon = Icons[slide.icon] || Icons.Sparkles;
  const lastSlide = idx === slides.length - 1;
  const lastChapter = chIdx === chapters.length - 1;

  const next = () => {
    if (!lastSlide) { setIdx((i) => i + 1); return; }
    const nd = markDone(ch.id);
    if (chapters.every((x) => nd.has(x.id))) { setView("menu"); return; }
    setView("menu");
  };
  const prev = () => { if (idx > 0) setIdx((i) => i - 1); else setView("menu"); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-overlay">
      <div dir={rtl ? "rtl" : "ltr"} className="bg-brand-cream w-full max-w-md rounded-2xl brutal border-2 border-brand-ink overflow-hidden flex flex-col max-h-[92vh]">
        <div className={`p-5 ${slide.color} relative`}>
          <button data-testid="onboarding-skip" onClick={() => onClose(allDone)}
            className="absolute top-3 end-3 bg-white/25 hover:bg-white/40 rounded-full p-1.5"><X className="w-4 h-4" strokeWidth={2.5} /></button>
          <button data-testid="onboarding-to-menu" onClick={() => setView("menu")}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest opacity-80 mb-2 hover:opacity-100">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />{c.chapters}
          </button>
          <div className="w-14 h-14 rounded-2xl bg-white/25 brutal-sm flex items-center justify-center mb-3">
            <Icon className="w-8 h-8" strokeWidth={2.5} />
          </div>
          <p className="text-xs font-bold uppercase tracking-widest opacity-80">{chIdx + 1}. {ch.title[lang]}</p>
          <h2 className="font-head font-black text-2xl leading-tight" data-testid="onboarding-title">{slide.title[lang]}</h2>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <p className="text-base leading-relaxed" data-testid="onboarding-body">{slide.body[lang]}</p>
        </div>

        <div className="p-4 border-t-2 border-brand-ink/10 bg-white">
          <div className="flex items-center gap-1 mb-3 justify-center" data-testid="onboarding-dots">
            {slides.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-brand-ink" : "w-1.5 bg-brand-ink/20"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="onboarding-prev" onClick={prev} className="brutal-sm brutal-press rounded-xl px-4 py-2.5 bg-white font-head font-bold flex items-center gap-1.5">
              <Back className="w-4 h-4" strokeWidth={2.5} />{idx > 0 ? t.prev : c.chapters}
            </button>
            <span className="text-xs font-bold text-brand-ink/50 flex-1 text-center">{t.step} {idx + 1} {t.of} {slides.length}</span>
            <button data-testid="onboarding-next" onClick={next} className="brutal brutal-press rounded-xl px-5 py-2.5 bg-brand-violet text-white font-head font-bold flex items-center gap-1.5">
              {lastSlide ? (lastChapter ? c.finish : c.nextCh) : t.next}{!lastSlide && <Fwd className="w-4 h-4" strokeWidth={2.5} />}
            </button>
          </div>
          <button data-testid="onboarding-later" onClick={() => onClose(allDone)}
            className="w-full text-center mt-2 text-xs font-bold text-brand-ink/50 hover:text-brand-ink/70">{c.later}</button>
        </div>
      </div>
    </div>
  );
}
